import { ApplicationInformation } from 'api/rtkQueryApi';
import { ECOAConsentType, StageType, BaseApplicationKeyInformationType, ApplicationStatus } from 'handlers/application';
import { ApplicationStep } from 'handlers/stages';
import { useEffect, useState } from 'react';

import { FundingStatus, compareLocalStorageFundingStatus, getFundingStatusFromLocalStorage } from './useFundingStatus';

const isStageComplete = (status: boolean | null | undefined) => typeof status === 'boolean';

const getCompletingVerificationStep = (application: ApplicationInformation['application']): ApplicationStep => {
  const { variables } = application;

  const bankAccountVerificationCompleted = variables[StageType.BankAccountVerificationComplete];
  const bankInformationManualReview =
    application.variables[BaseApplicationKeyInformationType.BankInformationManualReview];
  const incomeVerificationCompleted = variables[BaseApplicationKeyInformationType.IncomeVerificationComplete];
  const bankOrIncomeDocumentsUploaded = variables[BaseApplicationKeyInformationType.BankOrIncomeDocumentsUploaded];
  const submitApplicationCompleted = variables[StageType.SubmitApplicationComplete];
  const isCounterOffer =
    application.variables[BaseApplicationKeyInformationType.FinalOnlineOfferStatus] === 'Counter Offer';
  const isAutomatedFraudCheckFailed = application.variables.automated_fraud_check_complete === false;

  if (!isStageComplete(bankAccountVerificationCompleted)) {
    return ApplicationStep.Overview;
  }

  if (!bankAccountVerificationCompleted && !isStageComplete(bankInformationManualReview)) {
    return ApplicationStep.BankAccountVerificationNotAbleToVerify;
  }

  if (isStageComplete(bankAccountVerificationCompleted) && !isStageComplete(incomeVerificationCompleted)) {
    return ApplicationStep.IncomeSources;
  }

  if (isCounterOffer && incomeVerificationCompleted && bankAccountVerificationCompleted) {
    return ApplicationStep.CounterOffer;
  }

  if (
    (!bankAccountVerificationCompleted || !incomeVerificationCompleted) &&
    !isStageComplete(bankOrIncomeDocumentsUploaded) &&
    !(isAutomatedFraudCheckFailed && !bankAccountVerificationCompleted)
  ) {
    return ApplicationStep.DocumentsUpload;
  }

  if (!isStageComplete(submitApplicationCompleted)) {
    return ApplicationStep.SubmitApplication;
  }

  return ApplicationStep.Funding;
};

const getApplicationStep = (application: ApplicationInformation['application']): ApplicationStep => {
  const { status, variables } = application;
  const legalDisclosureStepCompleted = variables[ECOAConsentType.First] || variables[ECOAConsentType.Second];

  if (!isStageComplete(legalDisclosureStepCompleted)) {
    return ApplicationStep.ECOAConsent;
  }

  if (status === ApplicationStatus.ManualReview) {
    return ApplicationStep.ManualReview;
  }

  if (
    status === ApplicationStatus.SigningDocuments &&
    (!getFundingStatusFromLocalStorage(application.id) ||
      compareLocalStorageFundingStatus(FundingStatus.Sent, application.id))
  ) {
    return ApplicationStep.SigningDocuments;
  }

  if (status === ApplicationStatus.Rejected) {
    return ApplicationStep.Rejected;
  }

  if (status === ApplicationStatus.Canceled) {
    return ApplicationStep.ExpiredApplication;
  }

  if (
    status === ApplicationStatus.Approved ||
    compareLocalStorageFundingStatus(FundingStatus.Passed, application.id) ||
    compareLocalStorageFundingStatus(FundingStatus.Failed, application.id)
  ) {
    return ApplicationStep.Funding;
  }

  return ApplicationStep.Overview;
};

const useApplicationStep = (application: ApplicationInformation['application'] | undefined) => {
  const [applicationStep, setApplicationStep] = useState<ApplicationStep | null>(null);
  const [firstTime, setFirstTime] = useState<boolean>(true);

  useEffect(() => {
    if (application) {
      const { status } = application;
      if (!firstTime && status === ApplicationStatus.CompletingVerification) {
        const currentStep = getCompletingVerificationStep(application);
        setApplicationStep(currentStep);
      } else {
        setFirstTime(false);
        const currentStep = getApplicationStep(application);
        setApplicationStep(currentStep);
      }
    }
  }, [application]);
  return { applicationStep, setApplicationStep };
};

export default useApplicationStep;
